/**
 * Facts about one record as terms and values. `stacked` puts each value under its term, `columns`
 * is two columns of term and value from 640px and stacked below that, and `inline` puts term and
 * value on one line. A missing value reads "Not recorded", never a blank. A value marked `copy`
 * gets a button that copies it, for identifiers a person pastes elsewhere.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { IconButton } from "./IconButton.tsx";
import { CheckIcon, CopyIcon } from "./icons.tsx";
import type { Navigate } from "./Link.tsx";
import { Link } from "./Link.tsx";
import { textStyle } from "./textStyle.ts";
import { visuallyHidden } from "./visuallyHidden.ts";
const COPIED_MS = 2000;
const styles = {
  list: {
    k1xSpc: "xrvj5dj",
    kogj98: "x1ghz6dp",
    kOIVth: "x1r0x45p",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  stacked: {
    kumcoG: "x1mkdm3x",
    $$css: true
  },
  columns: {
    kumcoG: "x1mkdm3x x78gf5 x14ixu2r",
    $$css: true
  },
  inline: {
    kumcoG: "x1mkdm3x",
    kOIVth: "x1173whf",
    $$css: true
  },
  item: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x15xv0s5",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  itemInline: {
    kXwgrk: "xdt5ytf x1v667hp",
    kOIVth: "x15xv0s5 x10slnj3",
    $$css: true
  },
  term: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1lm7cez",
    $$css: true
  },
  termInline: {
    kUk6DE: "x3psx0u x11rimuf",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  value: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1173whf",
    kogj98: "x1ghz6dp",
    k7Eaqz: "xeuugli",
    kMwMTN: "xm8xz4y",
    kHjlTd: "xj0a0fe",
    $$css: true
  },
  missing: {
    kMwMTN: "xyjz3t7",
    $$css: true
  }
};
export interface DescriptionItem {
  readonly term: string;
  /** Leave out, or pass an empty string, for a value nobody has recorded. */
  readonly value?: string | undefined;
  /** Adds a button that copies the value. */
  readonly copy?: boolean;
  /** Makes the value a link. */
  readonly href?: string;
  /** Shown in place of `value` for a value that is more than text, such as a badge. */
  readonly content?: ReactNode;
}
export interface DescriptionListProps {
  readonly items: readonly DescriptionItem[];
  readonly variant?: "stacked" | "columns" | "inline";
  readonly navigate?: Navigate;
  readonly testId?: string;
}
function CopyValue({
  term,
  value
}: {
  readonly term: string;
  readonly value: string;
}) {
  const [outcome, setOutcome] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (outcome === "idle") return;
    const timer = window.setTimeout(() => setOutcome("idle"), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [outcome]);
  const said = outcome === "copied" ? `Copied ${term}` : outcome === "failed" ? `Couldn't copy ${term}` : "";
  return <>
      <IconButton label={said === "" ? `Copy ${term}` : said} icon={outcome === "copied" ? <CheckIcon /> : <CopyIcon />} variant="tertiary" size="sm" onClick={() => {
      Promise.resolve().then(() => navigator.clipboard.writeText(value)).then(() => setOutcome("copied"), () => setOutcome("failed"));
    }} />
      <span role="status" {...stylex.props(visuallyHidden.hidden)}>
        {said}
      </span>
    </>;
}
function Value({
  item,
  navigate
}: {
  readonly item: DescriptionItem;
  readonly navigate: Navigate | undefined;
}) {
  if (item.content !== undefined) return <span>{item.content}</span>;
  if (item.value === undefined || item.value === "") return <span className="xyjz3t7">Not recorded</span>;
  return <>
      <span>
        {item.href === undefined ? item.value : <Link href={item.href} variant="standalone" {...navigate ? {
        navigate
      } : {}}>
            {item.value}
          </Link>}
      </span>
      {item.copy === true ? <CopyValue term={item.term} value={item.value} /> : null}
    </>;
}
export function DescriptionList({
  items,
  variant = "columns",
  navigate,
  testId
}: DescriptionListProps) {
  return <dl data-testid={testId} {...stylex.props(styles.list, styles[variant])}>
      {items.map(item => <div key={item.term} {...{
      0: {
        className: "x78zum5 xdt5ytf x15xv0s5 xeuugli"
      },
      1: {
        className: "x78zum5 xeuugli xdt5ytf x1v667hp x15xv0s5 x10slnj3"
      }
    }[!!(variant === "inline") << 0]}>
          <dt {...stylex.props(textStyle.label, styles.term, variant === "inline" && styles.termInline)}>
            {item.term}
          </dt>
          <dd {...stylex.props(textStyle.body, styles.value)}>
            <Value item={item} navigate={navigate} />
          </dd>
        </div>)}
    </dl>;
}